import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Modal } from 'antd';
import styles from './AnnouncementModal.module.scss';
import { getActiveAnnouncements, dismissAnnouncement } from '../../reduxStore/actions/announcementActionsHelper';
import {
    announcementsData$,
    announcementsLoading$,
    announcementDismissing$
} from '../../reduxStore/selectors/announcementSelectors';

const AnnouncementModal = ({ isVisible, onClose }) => {
    const dispatch = useDispatch();
    const announcements = useSelector(announcementsData$);
    const loading = useSelector(announcementsLoading$);
    const dismissing = useSelector(announcementDismissing$);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [dontShowAgain, setDontShowAgain] = useState(false);

    useEffect(() => {
        if (isVisible) {
            dispatch(getActiveAnnouncements.request());
        }
    }, [isVisible, dispatch]);

    // Adjust currentIndex if it's out of bounds after dismissal
    useEffect(() => {
        if (currentIndex >= announcements.length && announcements.length > 0) {
            setCurrentIndex(announcements.length - 1);
        }
    }, [announcements.length, currentIndex]);

    const handleGotIt = () => {
        if (announcements.length === 0 || dismissing) return;

        const currentAnnouncement = announcements[currentIndex];
        const willDismiss = dontShowAgain;

        // Only dismiss if "Don't show again" is checked
        if (willDismiss) {
            dispatch(dismissAnnouncement.request({ announcementId: currentAnnouncement.id }));
            if (announcements.length > 1) {
                if (currentIndex >= announcements.length - 1) {
                    setCurrentIndex(Math.max(0, currentIndex - 1));
                }
                setDontShowAgain(false);
            } else {
                // This was the last announcement
                onClose();
            }
        } else {
            // Not dismissing, just navigate
            if (currentIndex < announcements.length - 1) {
                setCurrentIndex(currentIndex + 1);
                setDontShowAgain(false);
            } else {
                onClose();
            }
        }
    };

    const handlePrevious = () => {
        if (currentIndex > 0) {
            setCurrentIndex(currentIndex - 1);
            setDontShowAgain(false); // Reset checkbox when navigating
        }
    };

    const handleNext = () => {
        if (currentIndex < announcements.length - 1) {
            setCurrentIndex(currentIndex + 1);
            setDontShowAgain(false); // Reset checkbox when navigating
        }
    };

    const handleClose = () => {
        setDontShowAgain(false);
        onClose();
    };

    if (loading) {
        return null;
    }

    if (announcements.length === 0) {
        return (
            <Modal
                open={isVisible}
                onCancel={handleClose}
                footer={null}
                width={520}
                centered
                zIndex={10000}
                className={styles.announcementModal}
                closeIcon={
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                        <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                    </svg>
                }
            >
                <div className={styles.modalContent}>
                    <div className={styles.emptyState}>
                        <h2 className={styles.emptyTitle}>You&apos;re all caught up</h2>
                        <p className={styles.emptyText}>You have read everything. No new notifications right now.</p>
                    </div>

                    <div className={styles.modalFooter}>
                        <div className={`${styles.buttonGroup} ${styles.centerButtonGroup}`}>
                            <button
                                onClick={handleClose}
                                className={styles.dismissButton}
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            </Modal>
        );
    }

    // Safety check: if currentIndex is out of bounds, adjust it
    const safeIndex = Math.min(currentIndex, announcements.length - 1);
    const currentAnnouncement = announcements[safeIndex];

    // Additional safety check
    if (!currentAnnouncement) {
        return null;
    }

    const isFirstAnnouncement = safeIndex === 0;
    const isLastAnnouncement = safeIndex === announcements.length - 1;

    return (
        <Modal
            open={isVisible}
            onCancel={handleClose}
            footer={null}
            width={600}
            centered
            zIndex={10000}
            className={styles.announcementModal}
            closeIcon={
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                </svg>
            }
        >
            <div className={styles.modalContent}>
                {/* Loading Overlay */}
                {dismissing && (
                    <div className={styles.loadingOverlay}>
                        <div className={styles.spinner}></div>
                    </div>
                )}

                {/* Title */}
                <div className={styles.modalHeader}>
                    <h2 className={styles.title}>{currentAnnouncement.title}</h2>
                    {announcements.length > 1 && (
                        <div className={styles.counter}>
                            {currentIndex + 1} of {announcements.length}
                        </div>
                    )}
                </div>

                {/* Content */}
                <div className={styles.modalBody}>
                    <p className={styles.content}>{currentAnnouncement.content}</p>
                </div>

                {/* Checkbox */}
                <div className={styles.checkboxContainer}>
                    <label className={styles.checkboxLabel}>
                        <input
                            type="checkbox"
                            checked={dontShowAgain}
                            onChange={(e) => setDontShowAgain(e.target.checked)}
                            className={styles.checkbox}
                        />
                        <span className={styles.checkboxText}>
                            Don't show this again
                        </span>
                    </label>
                </div>

                {/* Footer */}
                <div className={styles.modalFooter}>
                    <div className={styles.buttonGroup}>
                        {/* Navigation Arrows */}
                        {announcements.length > 1 && (
                            <div className={styles.navigationButtons}>
                                <button
                                    onClick={handlePrevious}
                                    disabled={isFirstAnnouncement}
                                    className={`${styles.navButton} ${isFirstAnnouncement ? styles.disabled : ''}`}
                                    title="Previous"
                                >
                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                                        <path d="M15 18l-6-6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                </button>
                                <button
                                    onClick={handleNext}
                                    disabled={isLastAnnouncement}
                                    className={`${styles.navButton} ${isLastAnnouncement ? styles.disabled : ''}`}
                                    title="Next"
                                >
                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                                        <path d="M9 18l6-6-6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                </button>
                            </div>
                        )}

                        {/* Got it Button */}
                        <button
                            onClick={handleGotIt}
                            className={styles.dismissButton}
                            disabled={dismissing}
                        >
                            {dismissing ? (
                                <span className={styles.buttonSpinner}></span>
                            ) : (
                                isLastAnnouncement ? 'Got it!' : 'Next'
                            )}
                        </button>
                    </div>
                </div>
            </div>
        </Modal>
    );
};

export default AnnouncementModal;
